import { ref, provide, inject } from 'vue'
import type { Toast } from '@/components/Toast.vue'

// Interface pour le service de toast
export interface ToastService {
  addToast: (toast: Omit<Toast, 'id'>) => void
  success: (message: string, title?: string, options?: Partial<Toast>) => void
  error: (message: string, title?: string, options?: Partial<Toast>) => void
  warning: (message: string, title?: string, options?: Partial<Toast>) => void
  info: (message: string, title?: string, options?: Partial<Toast>) => void
  removeToast: (id: string) => void
  clearAll: () => void
}

// Clé d'injection
const TOAST_KEY = Symbol('toast')

// Service de toast par défaut (fallback)
const createDefaultToastService = (): ToastService => ({
  addToast: () => console.warn('Toast service not provided'),
  success: () => console.warn('Toast service not provided'),
  error: () => console.warn('Toast service not provided'),
  warning: () => console.warn('Toast service not provided'),
  info: () => console.warn('Toast service not provided'),
  removeToast: () => console.warn('Toast service not provided'),
  clearAll: () => console.warn('Toast service not provided')
})

// Fournir le service de toast
export const provideToast = (service: ToastService) => {
  provide(TOAST_KEY, service)
}

// Utiliser le service de toast
export const useToast = (): ToastService => {
  const service = inject<ToastService>(TOAST_KEY, createDefaultToastService())
  return service
}

// Hook pour créer un service de toast avec un composant
export const useToastService = () => {
  const toasts = ref<Toast[]>([])
  const timeouts = ref<Map<string, NodeJS.Timeout>>(new Map())

  const addToast = (toast: Omit<Toast, 'id'>) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9)
    const newToast: Toast = {
      id,
      ...toast,
      duration: toast.duration ?? 5000,
      persistent: toast.persistent ?? false
    }

    toasts.value.push(newToast)

    // Auto-suppression si pas persistant
    if (!newToast.persistent && newToast.duration && newToast.duration > 0) {
      const timeout = setTimeout(() => {
        removeToast(id)
      }, newToast.duration)
      timeouts.value.set(id, timeout)
    }
  }

  const removeToast = (id: string) => {
    const index = toasts.value.findIndex(toast => toast.id === id)
    if (index > -1) {
      toasts.value.splice(index, 1)
    }

    // Nettoyer le timeout
    const timeout = timeouts.value.get(id)
    if (timeout) {
      clearTimeout(timeout)
      timeouts.value.delete(id)
    }
  }

  const success = (message: string, title?: string, options?: Partial<Toast>) => {
    addToast({ type: 'success', message, title, ...options })
  }

  const error = (message: string, title?: string, options?: Partial<Toast>) => {
    addToast({ type: 'error', message, title, ...options })
  }

  const warning = (message: string, title?: string, options?: Partial<Toast>) => {
    addToast({ type: 'warning', message, title, ...options })
  }

  const info = (message: string, title?: string, options?: Partial<Toast>) => {
    addToast({ type: 'info', message, title, ...options })
  }

  const clearAll = () => {
    toasts.value.forEach(toast => {
      const timeout = timeouts.value.get(toast.id)
      if (timeout) {
        clearTimeout(timeout)
      }
    })
    timeouts.value.clear()
    toasts.value = []
  }

  return {
    toasts,
    timeouts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info,
    clearAll
  }
}
